'use client'

import { trackUsageEvent, type UsageEventProps } from '@/utils/posthog.utils'
import { devLog } from '@/utils/dev.utility'

export type SessionEventAction =
  | 'impersonation_denied'
  | 'login_submit'
  | 'login_success'
  | 'login_failure'
  | 'logout_started'
  | 'logout_completed'
  | 'public_auth_cleanup'
  | 'session_scope_rotated'
  | 'inactivity_warning_shown'
  | 'inactivity_warning_dismissed'

const SESSION_EVENT_PROPERTIES: Record<
  SessionEventAction,
  {
    action: string
    result?: string
  }
> = {
  impersonation_denied: {
    action: 'impersonation',
    result: 'denied',
  },
  login_submit: {
    action: 'login',
    result: 'started',
  },
  login_success: {
    action: 'login',
    result: 'success',
  },
  login_failure: {
    action: 'login',
    result: 'failure',
  },
  logout_started: {
    action: 'logout',
    result: 'started',
  },
  logout_completed: {
    action: 'logout',
    result: 'success',
  },
  public_auth_cleanup: {
    action: 'public_auth_cleanup',
    result: 'success',
  },
  session_scope_rotated: {
    action: 'session_scope_rotate',
    result: 'success',
  },
  inactivity_warning_shown: {
    action: 'inactivity_warning',
    result: 'shown',
  },
  inactivity_warning_dismissed: {
    action: 'inactivity_warning',
    result: 'dismissed',
  },
}

export function captureSessionEvent(
  action: SessionEventAction,
  reason?: string,
  diagnostics?: Omit<
    UsageEventProps<'auth_flow'>,
    'action' | 'surface' | 'result' | 'reason'
  >,
): void {
  const eventProperties = SESSION_EVENT_PROPERTIES[action]

  try {
    trackUsageEvent('auth_flow', {
      action: eventProperties.action,
      surface: 'session_auth',
      ...(eventProperties.result ? { result: eventProperties.result } : {}),
      ...(reason ? { reason } : {}),
      ...diagnostics,
    })
  } catch {}

  devLog(`[session] ${action}`, reason ? { reason } : {})
}
